import axios from 'axios';
import {store} from '../store';
import {AUTH_LOGOUT_REQUIRED} from '../actions/types';
import {Detection} from './detection';
import {MAX_API_RETRIES} from '../configs/constants';
import {rejoinAPIWarning} from '../configs/messages';

class Api {
  static get(route, data, params, mock = false) {
    return this.xhr(route, data, params, 'get', mock);
  }

  static put(route, data, params, mock = false) {
    return this.xhr(route, data, params, 'put', mock);
  }

  static post(route, data, params, mock = false) {
    return this.xhr(route, data, params, 'post', mock);
  }

  static delete(route, data, params, mock = false) {
    return this.xhr(route, data, params, 'delete', mock);
  }

  static replaceVariables(route, params) {
    Object.keys(params).forEach((key) => {
      route = route.replace(`:${key}`, params[key]);
    });
    return route;
  }
  static wrapApiErrors(error = {}) {
    const {response = {}} = error;
    const {status, data} = response;
    if (status === 401) {
      throw new Error(AUTH_LOGOUT_REQUIRED);
    }
    if (!data) {
      throw new Error('Unknown');
    }
    const {error: respErrorText} = data;
    throw new Error(respErrorText);
  }

  static xhr(route, data = {}, params = {}, method, useMock = false) {
    const state = store.getState();
    let numberRetries = 0;

    const sendRequest = (axiosInstance, mockInstance = false) => {
      const url = Api.replaceVariables(route, params);
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${state.auth.token}`,
      };
      const options = {
        baseURL: process.env.API_URL,
        url,
        method,
        headers,
      };
      if (method === 'get') {
        options.params = data;
      } else {
        options.data = data;
      }
      return axiosInstance(options)
        .then((res) => {
          if (mockInstance) {
            mockInstance.reset();
            mockInstance = false;
          }
          return res.data;
        })
        .catch(error => Api.wrapApiErrors(error));
    };

    if (process.env.__E2E__ || useMock) {
      return Promise.all([
        import('axios-mock-adapter'),
        import('../__mocks__/mockAxios'),
      ])
        .then((resolve) => {
          // axios-mock-adapter ^1.22+ ships the class as the default export
          const MockAdapterClass = resolve[0].default;
          const setupMocks = resolve[1].default;
          const instance = axios.create();
          const axiosMock = setupMocks(new MockAdapterClass(instance));
          return sendRequest(instance, axiosMock);
        });
    }
    const instance = axios.create();
    const sleepRequest = (milliseconds, originalRequest) => new Promise((resolve) => {
      numberRetries += 1;

      setTimeout(() => resolve(instance(originalRequest)), milliseconds);
    });

    instance.interceptors.response.use(response => response, (error) => {
      const {config, response = {}, code} = error;
      const originalRequest = config;

      if (code === 'ENOTFOUND') {
        return Promise.reject(error);
      }

      if (
        response.status === 400
        && Detection.isIOS
        && numberRetries <= MAX_API_RETRIES
        && response.data.error === rejoinAPIWarning
      ) {
        return sleepRequest(3000, originalRequest);
      }

      return Promise.reject(error);
    });

    return sendRequest(instance);
  }
}

export default Api;
